<template>
  <div data-cy-region="cart" class="container mx-auto">
    <h1
      v-if="count"
      class="[ px-6 pt-8 lg:p-0 lg:mt-4 ] text-xl-1 lg:text-5xl capitalize font-medium text-center lg:text-left"
    >
      {{ $t('cart') }}
    </h1>

    <p v-if="count" class="cart__itemsCounts px-6 lg:px-0 text-primary-2-04 text-center lg:text-left">
      {{ $t('productsCount', count, [count]) }}
    </p>

    <div class="relative mt-8">
      <EmptyMessage
        v-if="!count"
        data-attr="cartPage__emptyContainer"
        href="/"
        icon="cart-empty"
        :cta="$t('browseProducts')"
        :title="$t('emptyCart')"
        :message="$t('noItems')"
        class="mb-20 lg:mb-28 px-6 pt-8 lg:px-0"
      />

      <div v-else data-cy="cart-items" class="CartPage__container flex flex-col">
        <div>
          <aside
            v-if="configurableMessage && configurableMessage?.message && configurableMessage.message.length"
            class="CartPage__message flex flex-col gap-5 bg-primary-2-07 px-5 py-4 md:mb-10 mb-8 md:mx-0 mx-6 rounded-2xl text-base font-semibold"
          >
            <p>
              <svg-icon-discount class="inline-block mr-3 w-6 h-6" width="24" height="24" />

              {{ configurableMessage.message }}
            </p>

            <div v-if="configurableMessage.code">
              <button
                popovertarget="copyPopover"
                class="CartPage__message__copyButton w-fit flex px-8 py-3 gap-2 border border-black uppercase font-semibold rounded-full"
                @click="copyCode(configurableMessage.code)"
              >
                <svg-icon-copy class="w-6 h-6" width="24" height="24" />
                <span>{{ $t('copyCode') }}</span>
              </button>
              <div id="copyPopover" ref="popoverRef" popover class="popover--copy bg-transparent w-24 h-12">
                <span class="popover__message bg-primary-1-10 bg-opacity-30 px-2 py-1 rounded">{{ $t('copied') }}</span>
              </div>
            </div>
          </aside>

          <CartPriceRuleTiers v-if="showPriceRulesTiers" />
          <CartGiftTiers v-if="showGiftTiers" />
          <transition-group name="list" tag="ul" class="CartPage__List px-6 pb-8 lg:px-0">
            <ul>
              <li
                v-for="{
                  id,
                  quantity,
                  type,
                  name,
                  image,
                  oldPrice,
                  totalPrice,
                  totalDiscount,
                  stock,
                  sku,
                  cartControl,
                  isInvalidItem,
                  allowChangeQuantity,
                  url,
                  giftBoxMessage,
                  giftBoxOptionId,
                  configurationOptions,
                  isGift,
                } in items"
                :key="id"
              >
                <CartItem
                  :id="id"
                  data-attr="cartPage__item"
                  :quantity="quantity"
                  :name="name"
                  :image="image"
                  :price="totalPrice - totalDiscount"
                  :old-price="oldPrice * quantity"
                  :stock="stock"
                  :sku="sku"
                  :cart-control="cartControl"
                  :type="type"
                  :is-invalid-item="isInvalidItem"
                  :allow-change-quantity="allowChangeQuantity"
                  :url="url"
                  :gift-box-message="giftBoxMessage"
                  :gift-box-option-id="giftBoxOptionId"
                  :configuration-options="configurationOptions"
                  :is-gift="isGift"
                  updatable
                />
              </li>
            </ul>
          </transition-group>
        </div>
        <div class="hidden md:flex px-6 md:px-0">
          <CartPaymentSummary class="w-full" />
        </div>
      </div>
    </div>

    <section
      v-if="cartCrossSells.length"
      data-cy-region="suggested-products-slider"
      class="pb-16 lg:pb-25 mt-15 lg:mt-36 container mx-auto"
    >
      <h1 class="pl-6 mt:pl-0 text-xl-1 lg:text-4xl capitalize font-medium lg:text-center">
        {{ $t('mayLike') }}
      </h1>

      <ProductSlider id="suggested-products" v-slot="{ item }" class="mt-10" :items="cartCrossSells" arrows>
        <ProductCard :product="item" />
      </ProductSlider>
    </section>

    <div v-if="count" class="md:hidden px-6 md:px-0">
      <CartPaymentSummary class="w-full" />
    </div>

    <div v-if="count" class="ProceedCta fixed md:hidden w-full bottom-[84px] px-6 py-3 bg-primary-2-07 z-50">
      <div class="flex justify-between pb-2">
        <span class="text-system-gray text-sm">{{ $t('subtotal') }}</span>
        <Money class="font-bold" :value="subtotal" />
      </div>
      <AppButton
        data-cy="go-to-checkout"
        as="AppLink"
        :to="isLoggedIn ? '/checkout/shipping' : '/login?checkout=true'"
        class="block w-full font-medium uppercase"
        :disabled="hasInvalidItems"
        disable-hover
        inverted
      >
        {{ $t('proceed') }}
      </AppButton>
      <p v-if="hasInvalidItems" class="flex gap-x-1 items-center mt-1 ml-2">
        <svg-icon-error class="w-3 h-4 z-20 text-red-700" />
        <span class="text-red-700 text-base">{{ $t('invalidItemsError') }}</span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useExtendedStoreConfig } from '~/composables/cart';
const { isLoggedIn } = useAuth();
const { count, items, subtotal } = useCartAttributes();
const { hasInvalidItems } = useExtendedCartAttributes();
const { items: cartCrossSells } = useCartCrossProducts();
const { cartPromotions, cartPriceRuleDiscounts } = useExtendedCartAttributes();
const { getExtendedStoreConfig } = useExtendedStoreConfig();

const configurableMessage = ref<{ message: string | null | undefined; code?: string | null | undefined } | null>(null);
const popoverRef = ref<HTMLElement>();

const { t: $t } = useI18n({
  useScope: 'local',
});

onMounted(() => {
  // get gift tiers if exists
  useExtendedCartSetup();
  getExtendedStoreConfig().then(data => {
    configurableMessage.value = {
      message: data?.storeConfig?.configurableMessage,
      code: data?.storeConfig?.couponCode,
    };
  });
});

watch(subtotal, () => {
  useExtendedCartSetup();
});

const showGiftTiers = computed(() => {
  return Boolean(cartPromotions?.value?.options?.length);
});

const showPriceRulesTiers = computed(() => {
  return Boolean(cartPriceRuleDiscounts?.value?.tiers?.length);
});

function copyCode(code: string) {
  copyToClipboard(code);
  setTimeout(() => {
    popoverRef?.value?.hidePopover();
  }, 1500);
}

useAppSeoMeta({
  title: 'My Cart',
  description: 'Review your cart items before checking out',
});
</script>

<style lang="postcss" scoped>
.ProceedCta {
  box-shadow: 0px -6px 20px 2px rgba(26, 25, 25, 0.05);
}
.CartPage__container {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 80px;
}

@screen md {
  .CartPage__container {
    grid-template-columns: 1fr 406px;
    column-gap: 40px;
  }
}

.CartPage__List {
  li + li {
    margin-top: 30px;
  }

  li:not(:first-child) {
    @apply border-t border-deals-45;
    padding-top: 30px;
  }
}

.list-enter-active,
.list-leave-active {
  transition:
    opacity 0.3s ease-in-out,
    transform 0.3s ease-in-out;
}
.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translate3d(60%, 0, 0);
}

.list-leave-active {
  position: absolute;
}
.CartPage__message {
  &__copyButton {
    anchor-name: --copyCode;
  }

  .popover--copy {
    position-anchor: --copyCode;
    position-area: bottom;
    position: absolute;
    justify-self: anchor-center;
    margin-top: 0;
    left: anchor(right);
    top: anchor(bottom);
  }
  .popover__message {
    margin-bottom: 4px;
  }
  /* arrow */
  .popover__message::before {
    @apply border-b-8 border-primary-1-10 border-opacity-30;
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
  }
}
</style>

<i18n>
{
  "en": {
    "noItems": "No items in your cart yet. But with our wide selection, you'll find something you love.",
    "continueShopping": "Back to Continue Shopping",
    "mayLike": "You May Also Like!",
    "browseProducts": "Browse Products",
    "emptyCart": "Your Cart is Empty",
    "product": "Products",
    "cart": "My Cart",
    "productsCount": "You have {count} product in your cart | You have {count} products in your cart",
    "subtotal": "Subtotal",
    "proceed": "Proceed to checkout",
    "invalidItemsError": "Please remove out of stock items from cart.",
    "copyCode": "Copy Code",
    "copied": "Copied"
  },
  "ar": {
    "noItems": "لا توجد عناصر في عربة التسوق الخاصة بك حتى الآن. ولكن مع مجموعتنا الواسعة ، ستجد شيئًا تحبه.",
    "continueShopping": "متابعه التسوق",
    "mayLike": "!قد يعجبك ايضا",
    "browseProducts": "تصفح المنتجات",
    "emptyCart": "عربة التسوق فارغة",
    "cart": "عربة التسوق",
    "productsCount": "لديك {count} منتج فى السله ",
    "subtotal": "الاجمالي الفرعي",
    "proceed": "استمر للدفع",
    "invalidItemsError": "يرجي حذف المنتجات الغير متاحة من العربة.",
    "copyCode": "انسخ الكود",
    "copied": "تم النسخ"
  }
}
</i18n>
